import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogBody,
} from "@/components/ui/alert-dialog";
import { ColumnSettingsMenu } from "@/components/common/ColumnSettingsMenu";
import {
  CheckCircle2,
  Plus,
  RefreshCw,
  Server,
  Star,
  TrendingUp,
  type LucideIcon,
} from "lucide-react";
import { OSImageAdminTable } from "@/components/admin/os-images/OSImageAdminTable";
import { OSImageFilters } from "@/components/admin/os-images/OSImageFilters";
import { BulkActionsToolbar } from "@/components/admin/os-images/BulkActionsToolbar";
import { useAdminOSImageStore } from "@/stores/adminOSImageStore";
import { useOSImageFiltersStore } from "@/stores/osImageFiltersStore";
import {
  OS_IMAGE_COLUMNS,
  useOSImageColumnsStore,
} from "@/stores/osImageColumnsStore";
import {
  useAdminOSImages,
  useDeleteOSImage,
  useToggleOSImageActive,
  useBulkActivateOSImages,
  useBulkDeactivateOSImages,
  useBulkDeleteOSImages,
} from "@/hooks/useAdminOSImageQueries";
import { cn } from "@/lib/utils";
import type { AdminOSImage } from "@/types/admin";

/**
 * The whole catalog in one request, for the stat cards. The OS catalog is a curated table of tens
 * of rows, so the endpoint's maximum page covers all of it — which lets the cards count the real
 * catalog rather than only the rows on the current page, and stay put while the operator filters.
 */
const CATALOG_STATS_QUERY = { page: 1, pageSize: 100 } as const;

/**
 * Builds a page-number list with "ellipsis" sentinels for the pagination bar.
 * Always includes page 1 and the last page; collapses middle runs with "…".
 */
function getPaginationItems(
  currentPage: number,
  totalPages: number,
): (number | "ellipsis")[] {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }
  const items: (number | "ellipsis")[] = [1];
  if (currentPage > 3) items.push("ellipsis");
  const start = Math.max(2, currentPage - 1);
  const end = Math.min(totalPages - 1, currentPage + 1);
  for (let i = start; i <= end; i++) items.push(i);
  if (currentPage < totalPages - 2) items.push("ellipsis");
  items.push(totalPages);
  return items;
}

/** "Windows Server 2022" + "2022" would read as "Windows Server 2022 2022". */
function imageLabel(image: AdminOSImage): string {
  return image.version && !image.name.includes(image.version)
    ? `${image.name} ${image.version}`
    : image.name;
}

/** One stat tile. Every tile has the same structure so the row stays one height. */
function StatCard({
  icon: Icon,
  label,
  value,
  detail,
  toneClassName,
}: {
  icon: LucideIcon;
  label: string;
  value: string | number;
  detail?: string;
  toneClassName: string;
}) {
  return (
    <Card className="shadow-none">
      <CardContent className="flex h-full items-center gap-3 p-3">
        <div
          className={cn(
            "flex h-10 w-10 shrink-0 items-center justify-center rounded-lg",
            toneClassName,
          )}
        >
          <Icon className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <p className="text-xs text-muted-foreground">{label}</p>
          <p className="text-xl font-semibold leading-tight tabular-nums">
            {value}
          </p>
          <p className="truncate text-xs text-muted-foreground" title={detail}>
            {detail ?? " "}
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Admin OS image catalog — stat cards over the shared admin list-screen shape (see
 * ResourceMonitoringPage): one card holding the toolbar, the table and the pager, with filters and
 * page size cached per operator.
 *
 * There is no read-only detail page: the image name and the row menu both open the edit form.
 */
export function OSImagesPage() {
  const navigate = useNavigate();

  const filters = useOSImageFiltersStore((state) => state.filters);
  const page = useOSImageFiltersStore((state) => state.page);
  const pageSize = useOSImageFiltersStore((state) => state.pageSize);
  const setPage = useOSImageFiltersStore((state) => state.setPage);
  const setPageSize = useOSImageFiltersStore((state) => state.setPageSize);
  const clearCache = useOSImageFiltersStore((state) => state.clearCache);
  const hasActiveFilters = useOSImageFiltersStore((state) =>
    state.hasActiveFilters(),
  );

  const hiddenColumns = useOSImageColumnsStore((state) => state.hidden);
  const toggleColumn = useOSImageColumnsStore((state) => state.toggle);
  const showAllColumns = useOSImageColumnsStore((state) => state.showAll);

  const { selectedIds, toggleSelection, toggleAllSelection, clearSelection } =
    useAdminOSImageStore();

  const [imageToDelete, setImageToDelete] = useState<AdminOSImage | null>(null);

  const {
    data: response,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useAdminOSImages({ ...filters, page, pageSize });
  const { data: catalogResponse, refetch: refetchCatalog } =
    useAdminOSImages(CATALOG_STATS_QUERY);
  const deleteOSImage = useDeleteOSImage();
  const toggleActive = useToggleOSImageActive();
  const bulkActivate = useBulkActivateOSImages();
  const bulkDeactivate = useBulkDeactivateOSImages();
  const bulkDelete = useBulkDeleteOSImages();

  const images = response?.data ?? [];
  const totalPages = response?.pagination?.totalPages || 1;
  const currentPage = response?.pagination?.currentPage || 1;
  const totalItems = response?.pagination?.totalItems || 0;

  // Catalog-wide figures — independent of the filters and the page on screen.
  const catalog = catalogResponse?.data;
  const catalogTotal = catalogResponse?.pagination?.totalItems ?? 0;
  const activeCount = catalog?.filter((image) => image.isActive).length ?? 0;
  const featuredCount =
    catalog?.filter((image) => image.isFeatured).length ?? 0;
  const mostUsed = catalog?.reduce<AdminOSImage | null>(
    (top, image) =>
      image.usageCount > 0 && (!top || image.usageCount > top.usageCount)
        ? image
        : top,
    null,
  );

  // Bulk actions work on the ticked rows the operator can see. A tick left on another page or
  // under a filter that no longer matches is not acted on behind their back.
  const selectedImages = images.filter((image) => selectedIds.has(image.id));
  const selectedImageIds = selectedImages.map((image) => image.id);
  const isBulkPending =
    bulkActivate.isPending || bulkDeactivate.isPending || bulkDelete.isPending;

  const openImage = (image: AdminOSImage) =>
    navigate(`/admin/os-images/${image.id}/edit`);

  const confirmDelete = () => {
    if (!imageToDelete) return;
    deleteOSImage.mutate(imageToDelete.id, {
      onSuccess: () => {
        setImageToDelete(null);
        clearSelection();
      },
    });
  };

  // The screen's "start over": drops every filter, the page size, the cached copy in sessionStorage
  // and the row selection, then reloads the list and the figures above it.
  const handleRefresh = () => {
    clearCache();
    clearSelection();
    void refetch();
    void refetchCatalog();
  };

  // Only take the screen over when there is nothing to show. With rows in hand (placeholderData
  // keeps them) a failed refetch is a banner above the table, and the filter bar stays.
  if (error && !response) {
    return (
      <div className="flex h-96 flex-col items-center justify-center gap-4">
        <p className="text-destructive">Failed to load OS images</p>
        <Button variant="outline" onClick={() => refetch()}>
          Retry
        </Button>
      </div>
    );
  }

  const statValue = (value: number) => (catalog ? value : "—");

  return (
    <div className="flex h-full min-h-0 flex-col space-y-2">
      {/* Stat cards */}
      <div className="grid shrink-0 grid-cols-2 gap-2 lg:grid-cols-4">
        <StatCard
          icon={Server}
          label="Total OS Images"
          value={statValue(catalogTotal)}
          detail={
            catalog ? `${catalogTotal - activeCount} inactive` : undefined
          }
          toneClassName="bg-info-soft text-info-ink"
        />
        <StatCard
          icon={CheckCircle2}
          label="Active"
          value={statValue(activeCount)}
          toneClassName="bg-ok-soft text-ok-ink"
        />
        <StatCard
          icon={Star}
          label="Featured"
          value={statValue(featuredCount)}
          toneClassName="bg-warn-soft text-warn-ink"
        />
        <StatCard
          icon={TrendingUp}
          label="Most Used"
          value={mostUsed ? `${mostUsed.usageCount} VMs` : statValue(0)}
          detail={mostUsed ? imageLabel(mostUsed) : undefined}
          toneClassName="bg-brand-soft text-brand-ink"
        />
      </div>

      <Card className="relative flex min-h-0 flex-1 flex-col shadow-none">
        {/* Anchored to the card, not the viewport. */}
        {isFetching && !isLoading && (
          <div className="pointer-events-none absolute inset-0 z-40 flex items-center justify-center">
            <div className="flex items-center gap-2 rounded-lg border bg-background px-4 py-2 shadow-lg">
              <RefreshCw className="h-4 w-4 animate-spin text-primary" />
              <span className="text-sm text-muted-foreground">Updating...</span>
            </div>
          </div>
        )}

        <CardHeader className="shrink-0 p-2">
          <OSImageFilters
            actions={
              <Button
                onClick={() => navigate("/admin/os-images/create")}
                size="sm"
                className="h-8 min-h-8 shrink-0 gap-2 px-2 text-sm hover:translate-y-0 active:translate-y-0"
              >
                <Plus className="h-4 w-4" />
                New OS Image
              </Button>
            }
            trailing={
              <ColumnSettingsMenu
                columns={OS_IMAGE_COLUMNS}
                isVisible={(id) => !hiddenColumns.includes(id)}
                toggle={toggleColumn}
                showAll={showAllColumns}
                hiddenCount={hiddenColumns.length}
                className="h-8 min-h-8 px-2 hover:translate-y-0 active:translate-y-0"
              />
            }
          />
        </CardHeader>

        <CardContent
          aria-busy={isFetching}
          className="flex min-h-0 flex-1 flex-col p-2 pt-0"
        >
          {error && (
            <div className="mb-2 flex shrink-0 items-center justify-between gap-3 rounded-md border border-destructive/30 bg-destructive-soft px-3 py-2">
              <p className="text-sm text-destructive-ink">
                Could not refresh the list. Showing the last result that loaded.
              </p>
              <Button
                onClick={() => refetch()}
                variant="outline"
                size="sm"
                disabled={isFetching}
                className="h-7 shrink-0 px-2 text-xs"
              >
                Retry
              </Button>
            </div>
          )}

          <BulkActionsToolbar
            selectedImages={selectedImages}
            onActivate={() =>
              bulkActivate.mutate(selectedImageIds, {
                onSuccess: clearSelection,
              })
            }
            onDeactivate={() =>
              bulkDeactivate.mutate(selectedImageIds, {
                onSuccess: clearSelection,
              })
            }
            onDelete={() =>
              bulkDelete.mutate(selectedImageIds, { onSuccess: clearSelection })
            }
            onClearSelection={clearSelection}
            isLoading={isBulkPending}
          />

          {isLoading ? (
            <OSImageAdminTable
              images={images}
              isLoading
              selectedIds={selectedIds}
              onToggleSelection={toggleSelection}
              onToggleAllSelection={toggleAllSelection}
              onEdit={() => undefined}
              onToggleActive={() => undefined}
              onDelete={() => undefined}
            />
          ) : images.length === 0 ? (
            // Fills the card like the table does, so the panel keeps its height when a filter
            // empties the list, and the message sits where the rows were.
            <div className="flex min-h-0 flex-1 flex-col items-center justify-center py-16 text-center">
              <Server className="mx-auto mb-4 h-16 w-16 text-muted-foreground" />
              <h3 className="mb-2 text-lg font-semibold">
                {hasActiveFilters ? "No OS images found" : "No OS images yet"}
              </h3>
              <p className="text-muted-foreground">
                {hasActiveFilters
                  ? "Try adjusting or clearing your filters."
                  : "Create the first image to offer it in the VM wizard."}
              </p>
            </div>
          ) : (
            <OSImageAdminTable
              images={images}
              selectedIds={selectedIds}
              onToggleSelection={toggleSelection}
              onToggleAllSelection={toggleAllSelection}
              onEdit={openImage}
              onToggleActive={(image) =>
                toggleActive.mutate({ image, isActive: !image.isActive })
              }
              onDelete={setImageToDelete}
            />
          )}

          {/* Outside the three branches, so a zero-result search keeps its pager and page size. */}
          {!isLoading && (
            <div className="mt-auto flex shrink-0 flex-col items-center gap-2 pt-2 sm:flex-row sm:justify-between">
              <p className="text-sm text-muted-foreground">
                Page {currentPage} of {totalPages} ({totalItems} total)
              </p>
              <div className="flex max-w-full flex-wrap items-center justify-center gap-2">
                <Select
                  value={pageSize.toString()}
                  onValueChange={(value) => setPageSize(parseInt(value))}
                >
                  <SelectTrigger className="h-8 w-30 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10 per page</SelectItem>
                    <SelectItem value="20">20 per page</SelectItem>
                    <SelectItem value="50">50 per page</SelectItem>
                    <SelectItem value="100">100 per page</SelectItem>
                  </SelectContent>
                </Select>
                <div className="flex items-center gap-1 overflow-x-auto">
                  <Button
                    onClick={() => setPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1 || isFetching}
                    variant="outline"
                    size="sm"
                  >
                    Previous
                  </Button>
                  {getPaginationItems(currentPage, totalPages).map(
                    (item, index) =>
                      item === "ellipsis" ? (
                        <span
                          key={`ellipsis-${index}`}
                          className="px-1 text-sm text-muted-foreground"
                        >
                          …
                        </span>
                      ) : (
                        <Button
                          key={item}
                          onClick={() => setPage(item)}
                          disabled={isFetching}
                          variant={currentPage === item ? "default" : "outline"}
                          size="sm"
                          className="min-w-9"
                        >
                          {item}
                        </Button>
                      ),
                  )}
                  <Button
                    onClick={() =>
                      setPage(Math.min(totalPages, currentPage + 1))
                    }
                    disabled={currentPage === totalPages || isFetching}
                    variant="outline"
                    size="sm"
                  >
                    Next
                  </Button>
                </div>

                <Button
                  onClick={handleRefresh}
                  variant="outline"
                  size="sm"
                  disabled={isFetching}
                  title="Reset filters and reload"
                  aria-label="Reset filters and reload"
                  className="h-8 min-h-8 w-8 p-0 hover:translate-y-0 active:translate-y-0"
                >
                  <RefreshCw className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        open={imageToDelete !== null}
        onOpenChange={(open) => !open && setImageToDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete OS Image</AlertDialogTitle>
          </AlertDialogHeader>
          <AlertDialogBody>
            <AlertDialogDescription>
              Delete{" "}
              <strong>{imageToDelete ? imageLabel(imageToDelete) : ""}</strong>?
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogBody>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleteOSImage.isPending}
            >
              {deleteOSImage.isPending ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
